import {
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import { Notify, Report } from "notiflix";
import React, { useState } from "react";
import styled from "styled-components";
import { sendContactMessage } from "../../data/api/contact/ContactRoutes";

const INITIAL_FORM = {
  name: "",
  email: "",
  subject: "",
  phone: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const onChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // Limpiamos solo el error del campo que se está editando para que el
    // usuario tenga feedback inmediato cuando corrige.
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _omit, ...rest } = prev;
        return rest;
      });
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "El nombre es obligatorio.";
    if (!form.email.trim()) next.email = "El correo es obligatorio.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email.trim()))
      next.email = "El correo no tiene un formato válido.";
    if (!form.subject.trim()) next.subject = "El asunto es obligatorio.";
    if (!form.message.trim()) next.message = "El mensaje es obligatorio.";
    else if (form.message.trim().length < 10)
      next.message = "El mensaje debe tener al menos 10 caracteres.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) {
      Notify.failure("Revisa los campos resaltados antes de enviar.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await sendContactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      if (data?.success) {
        Report.success(
          "Mensaje enviado",
          data?.message ?? "Tu mensaje fue enviado correctamente.",
          "Aceptar"
        );
        setForm(INITIAL_FORM);
        setErrors({});
      } else {
        Notify.failure(data?.message ?? "No se pudo enviar el mensaje.");
      }
    } catch (error) {
      const status = error?.response?.status;
      const apiErrors = error?.response?.data?.errors;
      if (status === 422 && apiErrors) {
        // Mapeamos los errores del backend al estado local para que se
        // pinten directamente en cada TextField.
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, msgs]) => {
          mapped[key] = Array.isArray(msgs) ? msgs[0] : String(msgs);
        });
        setErrors(mapped);
        Notify.failure("Revisa los campos resaltados antes de enviar.");
      } else if (status === 429) {
        Notify.failure(
          "Has enviado demasiados mensajes en poco tiempo. Intenta de nuevo en un minuto."
        );
      } else {
        Notify.failure(
          error?.response?.data?.message ??
            "No se pudo enviar el mensaje. Intenta de nuevo más tarde."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #f8f8f8 0%, #ffffff 35%, #fafafa 100%)",
        minHeight: "100vh",
        py: { xs: 4, md: 7 },
      }}
    >
      <Container maxWidth="xl">
        {/* HERO */}
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: { xs: "24px", md: "32px" },
            background:
              "linear-gradient(135deg, #fff8f8 0%, #ffffff 45%, #fff4f4 100%)",
            border: "1px solid rgba(211,47,47,0.08)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.05)",
            mb: 6,
          }}
        >
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                p: { xs: 3, md: 6 },
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "999px",
                    backgroundColor: "rgba(211,47,47,0.08)",
                    color: "#d32f2f",
                    mb: 2,
                  }}
                >
                  <SupportAgentRoundedIcon fontSize="small" />
                  <Typography sx={{ fontWeight: 700, fontSize: ".92rem" }}>
                    Estamos para ayudarte
                  </Typography>
                </Box>

                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: { xs: "2.6rem", md: "4.5rem" },
                    lineHeight: 1,
                    fontWeight: 700,
                    color: "#1f1f1f",
                    mb: 2,
                  }}
                >
                  Contáctanos
                </Typography>

                <Typography
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.9,
                    fontSize: { xs: "1rem", md: "1.08rem" },
                    maxWidth: "760px",
                    textAlign: "justify",
                  }}
                >
                  En Fenabocol valoramos el contacto cercano con la comunidad.
                  Si tienes dudas, solicitudes, comentarios o deseas comunicarte
                  con nosotros por cualquier motivo, puedes hacerlo a través de
                  este formulario o por nuestros canales oficiales. Estaremos
                  atentos para orientarte y brindarte la información que
                  necesites.
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
              sx={{
                p: { xs: 3, md: 4 },
                display: "flex",
                alignItems: "stretch",
              }}
            >
              <HeroInfoCard>
                <Typography className="title">Canales oficiales</Typography>

                <Box className="info-item">
                  <Box className="icon">
                    <PlaceOutlinedIcon />
                  </Box>
                  <Typography>Calle 43a sur #78g - 28</Typography>
                </Box>

                <Box className="info-item">
                  <Box className="icon">
                    <MailOutlineIcon />
                  </Box>
                  <Box>
                    <Typography>fenabocolorg@gmail.com</Typography>
                    <Typography>febocolorg@gmail.com</Typography>
                  </Box>
                </Box>

                <Box className="info-item">
                  <Box className="icon">
                    <LocalPhoneOutlinedIcon />
                  </Box>
                  <Box>
                    <Typography>+57 300 2910614</Typography>
                    <Typography>+57 3028575623</Typography>
                  </Box>
                </Box>
              </HeroInfoCard>
            </Grid>
          </Grid>
        </Paper>

        {/* BLOQUES INFORMATIVOS */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <InfoCard>
              <Box className="icon-box">
                <CampaignOutlinedIcon />
              </Box>
              <Typography variant="h5" className="title">
                Atención institucional
              </Typography>
              <Typography className="description">
                Recibimos consultas, solicitudes de información, comentarios y
                mensajes relacionados con nuestra labor institucional.
              </Typography>
            </InfoCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <InfoCard>
              <Box className="icon-box">
                <MailOutlineIcon />
              </Box>
              <Typography variant="h5" className="title">
                Respuesta por canales oficiales
              </Typography>
              <Typography className="description">
                Puedes escribirnos mediante el formulario o directamente a
                nuestros correos electrónicos oficiales.
              </Typography>
            </InfoCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <InfoCard>
              <Box className="icon-box">
                <LocalPhoneOutlinedIcon />
              </Box>
              <Typography variant="h5" className="title">
                Contacto cercano
              </Typography>
              <Typography className="description">
                También contamos con líneas telefónicas y redes sociales para
                mantener una comunicación más directa con la comunidad.
              </Typography>
            </InfoCard>
          </Grid>
        </Grid>

        {/* FORMULARIO + INFORMACIÓN */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "28px",
                border: "1px solid rgba(0,0,0,0.06)",
                background: "linear-gradient(180deg, #ffffff 0%, #fcfcfc 100%)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                height: "100%",
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "2rem", md: "2.5rem" },
                  }}
                >
                  Envíanos un mensaje
                </Typography>

                <Typography
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.8,
                  }}
                >
                  Completa el formulario y nos pondremos en contacto contigo lo
                  antes posible.
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Nombre"
                      name="name"
                      variant="outlined"
                      value={form.name}
                      onChange={onChange("name")}
                      error={!!errors.name}
                      helperText={errors.name}
                      disabled={submitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Correo"
                      name="email"
                      type="email"
                      variant="outlined"
                      value={form.email}
                      onChange={onChange("email")}
                      error={!!errors.email}
                      helperText={errors.email}
                      disabled={submitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Asunto"
                      name="subject"
                      variant="outlined"
                      value={form.subject}
                      onChange={onChange("subject")}
                      error={!!errors.subject}
                      helperText={errors.subject}
                      disabled={submitting}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Teléfono"
                      name="phone"
                      variant="outlined"
                      value={form.phone}
                      onChange={onChange("phone")}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      disabled={submitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Mensaje"
                      name="message"
                      multiline
                      rows={7}
                      variant="outlined"
                      value={form.message}
                      onChange={onChange("message")}
                      error={!!errors.message}
                      helperText={errors.message}
                      disabled={submitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      loading={submitting}
                      loadingPosition="start"
                      startIcon={<SendRoundedIcon />}
                      sx={{
                        borderRadius: "999px",
                        px: 3.5,
                        py: 1.4,
                        fontWeight: 700,
                        textTransform: "none",
                        boxShadow: "0 10px 25px rgba(211,47,47,0.25)",
                      }}
                    >
                      <span>Enviar mensaje</span>
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={3} sx={{ height: "100%" }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "24px",
                  background: "#ffffff",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2.5,
                    color: "#1f1f1f",
                  }}
                >
                  Información de contacto
                </Typography>

                <Stack spacing={2}>
                  <ContactItem>
                    <Box className="icon">
                      <PlaceOutlinedIcon />
                    </Box>
                    <Typography>Calle 43a sur #78g - 28</Typography>
                  </ContactItem>

                  <ContactItem>
                    <Box className="icon">
                      <MailOutlineIcon />
                    </Box>
                    <Box>
                      <Typography>fenabocolorg@gmail.com</Typography>
                      <Typography>febocolorg@gmail.com</Typography>
                    </Box>
                  </ContactItem>

                  <ContactItem>
                    <Box className="icon">
                      <LocalPhoneOutlinedIcon />
                    </Box>
                    <Box>
                      <Typography>+57 300 2910614</Typography>
                      <Typography>+57 3028575623</Typography>
                    </Box>
                  </ContactItem>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  borderRadius: "28px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  backgroundColor: "#fff",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2.5,
                    color: "#1f1f1f",
                  }}
                >
                  Síguenos en
                </Typography>

                <SocialIcons>
                  <li>
                    <a
                      href="https://www.facebook.com/FederacionNacionalDeBomberosDeColombia/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FacebookIcon />
                    </a>
                  </li>

                  <li>
                    <a
                      href="https://tiktok.com/@fenabocolorg"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MusicNoteIcon />
                    </a>
                  </li>

                  <li>
                    <a
                      href="https://www.instagram.com/fenabocolorg/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <InstagramIcon />
                    </a>
                  </li>

                  <li>
                    <a
                      href="https://www.youtube.com/@fenabocolorg"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <YouTubeIcon />
                    </a>
                  </li>
                </SocialIcons>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;

const HeroInfoCard = styled.div`
  width: 100%;
  border-radius: 24px;
  padding: 24px 22px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);

  display: flex;
  flex-direction: column;
  justify-content: center;

  .title {
    font-size: 1.55rem;
    font-weight: 700;
    margin-bottom: 18px;
    color: #1f1f1f;
    line-height: 1.2;
  }

  .info-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;

    &:last-child {
      margin-bottom: 0;
    }

    .icon {
      width: 42px;
      height: 42px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(211, 47, 47, 0.08);
      color: #d32f2f;
      flex-shrink: 0;

      svg {
        font-size: 1.2rem;
      }
    }

    p {
      color: #555;
      margin: 0;
      line-height: 1.5;
      font-size: 0.98rem;
    }
  }
`;

const InfoCard = styled.div`
  height: 100%;
  padding: 24px;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 18px 36px rgba(0, 0, 0, 0.08);
  }

  .icon-box {
    width: 58px;
    height: 58px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(211, 47, 47, 0.1);
    color: #d32f2f;
    margin-bottom: 16px;
  }

  .title {
    font-weight: 700;
    margin-bottom: 8px;
    color: #1f1f1f;
  }

  .description {
    color: rgba(0, 0, 0, 0.65);
    line-height: 1.8;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(211, 47, 47, 0.08);
    color: #d32f2f;
    flex-shrink: 0;

    svg {
      font-size: 1.1rem;
    }
  }

  p {
    margin: 0;
    color: #555;
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const SocialIcons = styled.ul`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0;
  margin: 0;
  list-style: none;

  li a {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    background: rgba(211, 47, 47, 0.08);
    color: #d32f2f;
    transition: all 0.25s ease;

    &:hover {
      transform: translateY(-3px);
      background: #d32f2f;
      color: #fff;
      box-shadow: 0 10px 20px rgba(211, 47, 47, 0.2);
    }
  }
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 18px;
    background: #fff;

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: rgba(211, 47, 47, 0.35);
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #d32f2f;
      border-width: 2px;
    }
  }

  .MuiInputLabel-root.Mui-focused {
    color: #d32f2f;
  }
`;